import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { GRUPOS_HORAS, TIPOS_HORAS } from '@/pages/personal/nomina/utils/nomina';

const FMT_PESOS = '"$"#,##0';
const FMT_HORAS = '#,##0.00';

const suma = (obj, grupo) =>
  (GRUPOS_HORAS.find((g) => g.grupo === grupo)?.tipos || []).reduce((s, t) => s + (Number(obj?.[t.key]) || 0), 0);

const encabezado = (fila) => {
  fila.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  fila.eachCell((c) => {
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F3460' } };
    c.alignment = { vertical: 'middle', wrapText: true };
  });
  fila.height = 30;
};

const hoja = (wb, nombre, titulo, columnas) => {
  const ws = wb.addWorksheet(nombre, { views: [{ state: 'frozen', xSplit: 1, ySplit: 3 }] });
  ws.addRow([titulo]).font = { bold: true, size: 13 };
  ws.addRow([]);
  encabezado(ws.addRow(columnas.map((c) => c.titulo)));
  columnas.forEach((c, i) => {
    ws.getColumn(i + 1).width = c.ancho;
    if (c.fmt) ws.getColumn(i + 1).numFmt = c.fmt;
  });
  return ws;
};

/** Excel del reporte "Nómina del centro": hojas por quincena y por persona. */
export const exportarReporteNominaCentro = async (data, { centro }) => {
  if (!data) return;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'ERP Proflux';
  const titulo = `Nómina ${centro} · ${data.desde} a ${data.hasta}`;

  const wq = hoja(wb, 'Por quincena', titulo, [
    { titulo: 'Desde', ancho: 11 },
    { titulo: 'Hasta', ancho: 11 },
    { titulo: 'Completa', ancho: 9 },
    { titulo: 'Horas base', ancho: 9, fmt: FMT_HORAS },
    { titulo: 'Personas', ancho: 9 },
    { titulo: 'Horas trabajadas', ancho: 11, fmt: FMT_HORAS },
    { titulo: 'Horas con recargo', ancho: 11, fmt: FMT_HORAS },
    { titulo: 'Horas extra', ancho: 10, fmt: FMT_HORAS },
    { titulo: '$ Básico', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Auxilio de transporte', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Recargos', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Extras', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Devengado', ancho: 15, fmt: FMT_PESOS },
    { titulo: '$ Deducciones', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Neto', ancho: 15, fmt: FMT_PESOS },
    { titulo: '$ Aportes empleador', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Prestaciones', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Costo total empresa', ancho: 15, fmt: FMT_PESOS },
  ]);
  const filaQ = (q, etiqueta) => [
    etiqueta ?? q.desde,
    etiqueta ? '' : q.hasta,
    etiqueta ? '' : q.completa ? 'Sí' : 'Parcial',
    etiqueta ? '' : q.horasBase,
    q.personas,
    q.horas?.trabajadas || 0,
    suma(q.horas, 'Recargos'),
    suma(q.horas, 'Extras'),
    q.valores?.basico || 0,
    q.valores?.auxilioTransporte || 0,
    q.valores?.recargos || 0,
    q.valores?.extras || 0,
    q.devengado,
    q.deducciones,
    q.neto,
    q.costoEmpresa?.aportes?.total || 0,
    q.costoEmpresa?.prestaciones?.total || 0,
    q.costoEmpresa?.total || 0,
  ];
  data.quincenas.forEach((q) => wq.addRow(filaQ(q)));
  wq.addRow(filaQ(data.totales, 'TOTAL')).font = { bold: true };

  const wp = hoja(wb, 'Por persona', titulo, [
    { titulo: 'Persona', ancho: 32 },
    { titulo: 'Cédula', ancho: 13 },
    { titulo: 'Cargo', ancho: 18 },
    { titulo: 'Salario', ancho: 13, fmt: FMT_PESOS },
    { titulo: 'Quincenas', ancho: 9 },
    { titulo: 'Participación en el centro', ancho: 12, fmt: '0%' },
    { titulo: 'Días', ancho: 7, fmt: FMT_HORAS },
    { titulo: 'Horas trabajadas', ancho: 10, fmt: FMT_HORAS },
    ...TIPOS_HORAS.filter((t) => t.key !== 'ordinarias').map((t) => ({ titulo: `H. ${t.largo}`, ancho: 11, fmt: FMT_HORAS })),
    { titulo: '$ Básico', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Auxilio de transporte', ancho: 13, fmt: FMT_PESOS },
    { titulo: '$ Recargos', ancho: 13, fmt: FMT_PESOS },
    { titulo: '$ Extras', ancho: 13, fmt: FMT_PESOS },
    { titulo: '$ Incapacidad', ancho: 12, fmt: FMT_PESOS },
    { titulo: '$ Devengado', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Deducciones', ancho: 13, fmt: FMT_PESOS },
    { titulo: '$ Neto', ancho: 14, fmt: FMT_PESOS },
    { titulo: '$ Aportes empleador', ancho: 13, fmt: FMT_PESOS },
    { titulo: '$ Prestaciones', ancho: 13, fmt: FMT_PESOS },
    { titulo: '$ Costo total empresa', ancho: 14, fmt: FMT_PESOS },
    { titulo: 'Advertencias', ancho: 60 },
  ]);
  for (const p of data.personas) {
    wp.addRow([
      p.empleado?.nombre,
      p.empleado?.cedula || '',
      p.empleado?.rol || '',
      p.salario,
      p.quincenas,
      p.participacion || 0,
      p.diasLaborados,
      p.horas.trabajadas,
      ...TIPOS_HORAS.filter((t) => t.key !== 'ordinarias').map((t) => p.horas[t.key] || 0),
      p.valores.basico || 0,
      p.valores.auxilioTransporte || 0,
      p.valores.recargos || 0,
      p.valores.extras || 0,
      p.valores.incapacidad || 0,
      p.devengado,
      p.deducciones,
      p.neto,
      p.costoEmpresa?.aportes?.total || 0,
      p.costoEmpresa?.prestaciones?.total || 0,
      p.costoEmpresa?.total || 0,
      (p.advertencias || []).join(' | '),
    ]);
  }

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `Nomina_${centro.replace(/[^\w-]+/g, '_')}_${data.desde}_a_${data.hasta}.xlsx`,
  );
};
